/**
 * Single-expand coordinator for hover/pin columns (SecondaryColumn).
 *
 * Kept in a standalone module (no React, no component imports) so BOTH
 * SecondaryColumn and Sidebar can use it without creating a circular import
 * (Sidebar → SecondaryColumn → AppLayout → Sidebar).
 *
 * Each column registers `{ id, collapse() }`. Opening one column collapses the
 * others so only one is ever expanded; the main menu calls
 * `collapseAllColumns()` on hover so entering it collapses every column too.
 */
const columnRegistry = new Set();

// ── Portal-popover hold (Session 271) ────────────────────────────────────────
// A popover rendered in a PORTAL (the DateInput calendar, e.g. the clients
// column's "Reviewed until" picker) lives outside the column's DOM, so moving
// the pointer into it reads as "not hovering the column" and the column
// auto-collapses — taking the popover with it mid-pick. While such a popover
// is open it holds every column: collapse timers are paused, the
// click-outside collapse is suppressed, and columns no longer expand/collapse
// each other (crossing another column's rail on the way to the calendar must
// not close the one you came from).
let holdDepth = 0;

export function holdColumns(active) {
  holdDepth = Math.max(0, holdDepth + (active ? 1 : -1));
  const held = holdDepth > 0;
  columnRegistry.forEach((l) => l.hold && l.hold(held));
}

export function columnsHeld() {
  return holdDepth > 0;
}

export function registerColumn(listener) {
  columnRegistry.add(listener);
  return () => { columnRegistry.delete(listener); };
}

export function announceColumnExpand(id) {
  if (columnsHeld()) return;
  columnRegistry.forEach((l) => { if (l.id !== id) l.collapse(); });
}

export function collapseAllColumns() {
  if (columnsHeld()) return;
  columnRegistry.forEach((l) => l.collapse());
}
